import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Text,
  BlockStack,
  Button,
  Banner,
  Spinner,
  InlineStack,
  Badge,
  EmptyState,
} from "@shopify/polaris";
import PrettyDate from "components/format/PrettyDate";
import {
  fetchDownloadOrders,
  fetchDownloadOrder,
  downloadOrderZip,
  DownloadOrder,
  arrangeParentAndChildRows,
} from "features/document-cart/document-cart.service";
import { socket } from "services/socket-client";
import { toast } from "sonner";
import "./download-history-tab.scss";

interface DownloadHistoryTabProps {
  companyId: string;
  userId: string;
}

interface DownloadOrderReadyPayload {
  orderId: string;
  orderNumber?: string;
}

interface DownloadOrderFailedPayload {
  orderId: string;
  message?: string;
}

function statusTone(
  status: string
): "success" | "critical" | "attention" | "info" | undefined {
  switch (status) {
    case "ready":
      return "success";
    case "failed":
      return "critical";
    case "processing":
    case "pending":
      return "attention";
    case "expired":
      return "info";
    default:
      return undefined;
  }
}

const DownloadHistoryTab: React.FC<DownloadHistoryTabProps> = ({
  companyId,
  userId,
}) => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<DownloadOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(
    () => new Set()
  );
  const orderUpdatesRef = useRef<Record<string, Partial<DownloadOrder>>>({});
  const itemsCacheRef = useRef<Record<string, DownloadOrder["items"]>>({});
  const fetchInFlightRef = useRef<Record<string, Promise<void> | undefined>>({});

  const applyOrderUpdate = useCallback(
    (orderId: string, update: Partial<DownloadOrder>) => {
      const key = String(orderId);
      orderUpdatesRef.current[key] = {
        ...orderUpdatesRef.current[key],
        ...update,
      };
      setOrders((prev) =>
        prev.map((order) =>
          String(order.id) === key
            ? { ...order, ...orderUpdatesRef.current[key] }
            : order
        )
      );
    },
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDownloadOrders(companyId, {
        page: 1,
        pageSize: 50,
        userId,
      });
      const next = (data.orders || []).map((order) => {
        const cachedItems = itemsCacheRef.current[String(order.id)];
        return {
          ...order,
          ...orderUpdatesRef.current[String(order.id)],
          items: order.items?.length
            ? order.items
            : cachedItems || order.items,
        };
      });
      setOrders(next);
    } catch {
      toast.error(t("document_cart_history_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onReady = (...args: unknown[]) => {
      const payload = args[0] as DownloadOrderReadyPayload | undefined;
      if (!payload?.orderId) return;

      const update: Partial<DownloadOrder> = { status: "ready" };
      if (payload.orderNumber) {
        update.orderNumber = payload.orderNumber;
      }
      applyOrderUpdate(payload.orderId, update);
    };

    const onFailed = (...args: unknown[]) => {
      const payload = args[0] as DownloadOrderFailedPayload | undefined;
      if (!payload?.orderId) return;

      const update: Partial<DownloadOrder> = { status: "failed" };
      if (payload.message) {
        update.errorMessage = payload.message;
      }
      applyOrderUpdate(payload.orderId, update);
    };

    const onProgress = (...args: unknown[]) => {
      const payload = args[0] as { orderId: string; status: string } | undefined;
      if (!payload?.orderId) return;
      setOrders((prev) =>
        prev.map((order) => {
          if (String(order.id) === String(payload.orderId)) {
            if (["ready", "failed", "expired"].includes(order.status)) {
              return order;
            }
            return { ...order, status: payload.status };
          }
          return order;
        })
      );
    };

    socket.on("download-order-ready", onReady);
    socket.on("download-order-failed", onFailed);
    socket.on("download-order-progress", onProgress);

    return () => {
      socket.off("download-order-ready", onReady);
      socket.off("download-order-failed", onFailed);
      socket.off("download-order-progress", onProgress);
    };
  }, [applyOrderUpdate]);

  const ensureItemsLoaded = useCallback(
    async (orderId: string) => {
      const key = String(orderId);

      if (itemsCacheRef.current[key]?.length) {
        setOrders((prev) =>
          prev.map((o) =>
            String(o.id) === key && !o.items?.length
              ? { ...o, items: itemsCacheRef.current[key] }
              : o
          )
        );
        return;
      }

      if (fetchInFlightRef.current[key]) {
        await fetchInFlightRef.current[key];
        return;
      }

      setDetailLoadingId(orderId);
      const request = (async () => {
        try {
          const { order } = await fetchDownloadOrder(companyId, orderId);
          const items = order.items || [];
          itemsCacheRef.current[key] = items;
          setOrders((prev) =>
            prev.map((o) =>
              String(o.id) === key
                ? {
                    ...o,
                    fileCount: order.fileCount ?? o.fileCount,
                    status: order.status || o.status,
                    items,
                    ...orderUpdatesRef.current[key],
                  }
                : o
            )
          );
        } catch {
          toast.error(t("document_cart_history_load_failed"));
        } finally {
          setDetailLoadingId((current) =>
            current === orderId ? null : current
          );
          delete fetchInFlightRef.current[key];
        }
      })();

      fetchInFlightRef.current[key] = request;
      await request;
    },
    [companyId, t]
  );

  const toggleExpand = (orderId: string) => {
    if (expandedId === orderId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(orderId);
    const existing = orders.find((o) => o.id === orderId);
    if (existing?.items?.length) {
      itemsCacheRef.current[String(orderId)] = existing.items;
      return;
    }
    void ensureItemsLoaded(orderId);
  };

  const handleDownload = async (order: DownloadOrder) => {
    if (downloadingIds.has(order.id)) return;
    setDownloadingIds((current) => new Set(current).add(order.id));
    try {
      await downloadOrderZip(
        companyId,
        order.id,
        order.orderNumber ? `${order.orderNumber}.zip` : undefined
      );
      toast.success(t("document_cart_download_started"));
    } catch {
      toast.error(t("document_cart_download_failed"));
    } finally {
      setDownloadingIds((current) => {
        const next = new Set(current);
        next.delete(order.id);
        return next;
      });
    }
  };

  if (loading && !orders.length) {
    return (
      <Card>
        <div className="download-history-loading">
          <Spinner size="large" />
          <Text as="p" tone="subdued" variant="bodySm">
            {t("document_cart_history_loading")}
          </Text>
        </div>
      </Card>
    );
  }

  if (!loading && !orders.length) {
    return (
      <Card>
        <EmptyState
          heading={t("document_cart_history_empty")}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <Text as="p">{t("document_cart_history_empty_hint")}</Text>
        </EmptyState>
      </Card>
    );
  }

  return (
    <BlockStack gap="400">
      <Banner tone="info">{t("document_cart_history_snapshot_hint")}</Banner>

      <Card padding="0">
        <ul className="download-history-list">
          {orders.map((order) => {
            const expanded = expandedId === order.id;
            const isReady = order.status === "ready";
            const isDownloading = downloadingIds.has(order.id);
            const isLoadingFiles = detailLoadingId === order.id;
            const items =
              order.items || itemsCacheRef.current[String(order.id)] || [];

            return (
              <li key={order.id} className="download-history-row">
                <div className="download-history-row__main">
                  <div className="download-history-row__meta">
                    <InlineStack gap="200" blockAlign="center" wrap>
                      <Text as="h3" variant="headingSm" fontWeight="semibold">
                        {order.orderNumber}
                      </Text>
                      <InlineStack gap="100" blockAlign="center">
                        <Badge tone={statusTone(order.status)}>
                          {t(`document_cart_status_${order.status}`, {
                            defaultValue: order.status,
                          })}
                        </Badge>
                        {(order.status === "processing" || order.status === "pending") && (
                          <Spinner size="small" />
                        )}
                      </InlineStack>
                    </InlineStack>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {order.createdAt ? (
                        <PrettyDate
                          dateString={
                            typeof order.createdAt === "number"
                              ? new Date(order.createdAt).toISOString()
                              : String(order.createdAt)
                          }
                          showTime
                        />
                      ) : (
                        "—"
                      )}
                      {" · "}
                      {t("document_cart_folder_file_count", {
                        count: order.fileCount,
                      })}
                    </Text>
                    {order.status === "failed" && order.errorMessage ? (
                      <Text as="p" tone="critical" variant="bodySm">
                        {order.errorMessage}
                      </Text>
                    ) : null}
                  </div>

                  <InlineStack gap="200" wrap={false}>
                    <Button
                      variant="plain"
                      onClick={() => toggleExpand(order.id)}
                      disclosure={expanded ? "up" : "down"}
                    >
                      {expanded
                        ? t("document_cart_hide_files")
                        : t("document_cart_show_files")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => void handleDownload(order)}
                      disabled={!isReady || isDownloading}
                      loading={isDownloading}
                    >
                      {isDownloading
                        ? t("document_cart_downloading")
                        : t("download")}
                    </Button>
                  </InlineStack>
                </div>

                <div
                  className={`download-history-files${
                    expanded ? " is-open" : ""
                  }`}
                  aria-hidden={!expanded}
                >
                  <div className="download-history-files__inner">
                    <div className="download-history-files__content">
                      {isLoadingFiles && !items.length ? (
                        <div className="download-history-files__loading">
                          <Spinner size="small" />
                          <Text as="span" tone="subdued" variant="bodySm">
                            {t("document_cart_files_loading")}
                          </Text>
                        </div>
                      ) : items.length ? (
                        <ul className="download-history-file-list">
                          {arrangeParentAndChildRows(items).map(
                            ({ item, isChild, depth }) => (
                              <li
                                key={item.id}
                                className={
                                  isChild
                                    ? "download-history-file-list__item--child"
                                    : undefined
                                }
                                style={
                                  depth > 0
                                    ? { marginLeft: `${depth * 20}px` }
                                    : undefined
                                }
                              >
                                <span className="download-history-file-list__title">
                                  {item.fileTitle || item.fileId}
                                </span>
                                {item.categoryPath ? (
                                  <span className="download-history-file-list__path">
                                    {item.categoryPath}
                                  </span>
                                ) : null}
                              </li>
                            )
                          )}
                        </ul>
                      ) : (
                        <Text as="p" tone="subdued" variant="bodySm">
                          {t("document_cart_no_files_in_pack")}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    </BlockStack>
  );
};

export default DownloadHistoryTab;
